/**
 * 12-Week Hypertrophy Builder
 *
 * For users who pick the `body-comp` goal at onboarding and don't want
 * HST's aggressive deficit framework. This is a sustainable, moderate-
 * intensity hypertrophy program — build muscle, keep cardio for health,
 * let the calorie system handle deficit/surplus per the user's stated
 * bodyCompGoal.
 *
 * Structure: Upper/Lower split four times per week plus one easy cardio
 * day. The two upper days prioritize push and pull respectively; the two
 * lower days prioritize squat and hinge respectively. Reps live in the
 * 8-15 range — heavier on compounds (8-10), higher on isolation work
 * (10-15) — with two block-shifts that keep the stimulus from staling.
 *
 * What this is NOT:
 * - A pure strength program (use Pure Strength 12-Week)
 * - HST (more aggressive cycling, rep ranges shift faster, narrower)
 * - A tactical program (no rucks, no conditioning intensity)
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'hypertrophy-builder-12week',
  name: '12-Week Hypertrophy Builder',
  description: 'Moderate-intensity hypertrophy with an upper/lower split. Reps in the 8-15 range, two block-shifts to keep stimulus fresh, one easy cardio day for health and recovery.',
  level: 'grunt',
  duration: 12,
  daysPerWeek: 5,
  goals: ['body-comp'],
  difficulty: 'intermediate',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
};

const periodization: ProgramPeriodizationModel = {
  style: 'block',
  phases: [
    // Week 1 + Week 12: Testing — bodyweight + AMRAP markers + 8RM lift maxes.
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Hypertrophy Testing',
      focus: 'Bodyweight + 8RM on the four main lifts; AMRAP pull-ups and push-ups',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
    // Weeks 2-5: Strength Block — 6-10 reps on compounds, 10-12 on
    // accessories. Loads rise across the block but stay sub-max.
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Strength Block',
      focus: 'Heavier compounds (6-10 reps) drive the muscle gain ceiling.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.70,
        endIntensity: 0.78,
        weeklyIncrease: 0.02,
      },
      trainingParameters: {
        repRange: [6, 10],
        setsMultiplier: 1.0,
        restPeriod: '90-120s',
      },
    },
    // Week 6: Cutback — drops volume 30% to flush accumulated fatigue
    // before the higher-rep, higher-volume Hypertrophy Block.
    {
      weeks: [6],
      type: 'deload',
      name: 'Cutback Week',
      focus: 'Reduce volume 30% — recover, then attack the volume block.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.7,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.65,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [8, 10],
        setsMultiplier: 0.7,
        restPeriod: '90s',
      },
    },
    // Weeks 7-10: Hypertrophy Block — 10-15 reps, RPE-paced. Higher
    // volume, slightly lower intensity. This is where the muscle gain
    // signal compounds.
    {
      weeks: [7, 8, 9, 10],
      type: 'accumulation',
      name: 'Hypertrophy Block',
      focus: 'Higher reps (10-15), more total volume, RPE 8 cap.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.1,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.72,
        weeklyIncrease: 0.0175,
      },
      trainingParameters: {
        repRange: [10, 15],
        setsMultiplier: 1.1,
        restPeriod: '60-90s',
      },
    },
    // Week 11: Light Week — half volume going into the W12 retest.
    {
      weeks: [11],
      type: 'deload',
      name: 'Light Week',
      focus: 'Half volume, maintain intensity. Sharpen for retest.',
      intensityMultiplier: 0.9,
      volumeMultiplier: 0.5,
      progression: {
        type: 'linear',
        startIntensity: 0.70,
        endIntensity: 0.70,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [8, 10],
        setsMultiplier: 0.5,
        restPeriod: '90s',
      },
    },
  ],
};

// BASE TEMPLATES

const testingDay: WorkoutTemplateDay = {
  name: 'Hypertrophy Test',
  sessionType: 'assessment',
  estimatedDuration: 85,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before max-effort lifting' },
        { name: 'Hip Opener Flow', sets: 2, reps: 10, format: 'recovery', details: 'Open hips before squat + RDL testing' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Wake up upper back for pushes and pulls' },
      ],
    },
    {
      name: 'Bodyweight Markers',
      type: 'testing',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Max strict reps in 2 minutes', sessionType: 'assessment' },
        { name: 'Pull-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Max strict reps, no kipping', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Program Maxes',
      type: 'testing',
      exercises: [
        { name: 'Back Squat', sets: 1, reps: '8RM', intensity: 'Max', details: 'Work up to a heavy 8-rep top set. Brzycki estimates 1RM.', sessionType: 'assessment' },
        { name: 'Bench Press', sets: 1, reps: '8RM', intensity: 'Max', details: 'Pause briefly on chest, full lockout.', sessionType: 'assessment' },
        { name: 'Romanian Deadlift', sets: 1, reps: '8RM', intensity: 'Max', details: 'Hinge pattern, slight knee bend. The hamstring/glute lift drives lower-body hypertrophy.', sessionType: 'assessment' },
        { name: 'Overhead Press', sets: 1, reps: '8RM', intensity: 'Max', details: 'Strict press from rack, no leg drive.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 10, format: 'recovery', details: 'Decompress hips and spine after heavy testing' },
      ],
    },
  ],
};

// Day 1 — Upper Push. Bench leads, then incline for upper-chest emphasis,
// then shoulders/triceps. 4 exercises that cover horizontal + vertical
// pressing planes.
const upperPushDay: WorkoutTemplateDay = {
  name: 'Upper Body — Push',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Thoracic mobility before pressing' },
        { name: 'Band Pull-Aparts', sets: 3, reps: 15, format: 'recovery', details: 'Wake up rear delts and upper back — supports the bench arch and OHP stack' },
      ],
    },
    {
      name: 'Compound Push',
      type: 'strength',
      exercises: [
        { name: 'Bench Press', sets: 4, reps: 8, intensity: 'progressive', details: 'Pause briefly on chest. Top set is the hardest.' },
        { name: 'Overhead Press', sets: 3, reps: 8, intensity: 'progressive', details: 'Strict, full ROM. Add Push Press only if shoulders are healthy.' },
      ],
    },
    {
      name: 'Hypertrophy Push',
      type: 'strength',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 3, reps: 12, intensity: 'RPE 8', details: '30-45° incline. Controlled tempo, full stretch at the bottom.' },
        { name: 'Lateral Raise', sets: 3, reps: 15, intensity: 'RPE 8', details: 'Light dumbbells. Pause at the top, controlled negatives.' },
        { name: 'Tricep Pushdown', sets: 3, reps: 12, intensity: 'RPE 8', details: 'Lockout focus. Keep elbows pinned to your sides.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress the spine after overhead loading' },
      ],
    },
  ],
};

// Day 2 — Lower (Squat-dominant). Squat leads, then accessories that
// add quad volume without retaxing the same plane.
const lowerSquatDay: WorkoutTemplateDay = {
  name: 'Lower Body — Squat',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before squatting heavy' },
        { name: 'Hip Opener Flow', sets: 2, reps: 10, format: 'recovery', details: 'Deep hip opening before back squat and unilateral work' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Upper back lock-in for the squat brace' },
      ],
    },
    {
      name: 'Compound Squat',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 4, reps: 8, intensity: 'progressive', details: 'Below parallel if mobility allows. Brace hard, drive through midfoot.' },
      ],
    },
    {
      name: 'Hypertrophy Lower',
      type: 'strength',
      exercises: [
        { name: 'Leg Press', sets: 3, reps: 12, intensity: 'RPE 8', details: 'High foot placement biases glutes/hams; low placement biases quads. Pick one and stick with it.' },
        { name: 'Bulgarian Split Squat', sets: 3, reps: 10, intensity: 'RPE 8', details: 'Per leg, rear foot elevated. The single-leg work that exposes asymmetries.' },
        { name: 'Leg Curl', sets: 3, reps: 12, intensity: 'RPE 8', details: 'Slow eccentric — 3 seconds down. Hamstrings respond to time under tension.' },
        { name: 'Standing Calf Raises', sets: 4, reps: 15, intensity: 'RPE 8', details: 'Pause at the top stretch and contraction. Standing (straight-knee) biases the gastrocnemius.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 10, format: 'recovery', details: 'Decompress hips and spine after heavy lower work' },
      ],
    },
  ],
};

// Day 3 — Upper Pull. Pull-Ups lead, then horizontal pull, then face
// pulls and biceps. Pulling volume is roughly matched to the push day's
// pressing volume.
const upperPullDay: WorkoutTemplateDay = {
  name: 'Upper Body — Pull',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before pulling' },
        { name: 'Band Pull-Aparts', sets: 3, reps: 15, format: 'recovery', details: 'Activate upper back and rear delts before rowing and pull-ups' },
      ],
    },
    {
      name: 'Compound Pull',
      type: 'strength',
      exercises: [
        { name: 'Pull-Ups', sets: 4, reps: 'AMRAP', format: 'bodyweight', details: 'Strict reps to RPE 9. Add weight once you hit 12+ clean reps. Use Assisted Pull-Up if you can\'t hit 5+ yet.' },
        { name: 'Barbell Row', sets: 4, reps: 8, intensity: 'progressive', details: 'Pendlay-style — pull from the floor each rep. Strict, no torso swing.' },
      ],
    },
    {
      name: 'Hypertrophy Pull',
      type: 'strength',
      exercises: [
        { name: 'Dumbbell Row', sets: 3, reps: 12, intensity: 'RPE 8', details: 'One arm at a time. Full stretch at the bottom, squeeze the shoulder blade at the top.' },
        { name: 'Face Pull', sets: 3, reps: 15, intensity: 'RPE 7', details: 'Cable or band. Aim for the face, pull elbows wide. Rear delt + posture insurance.' },
        { name: 'Hammer Curl', sets: 3, reps: 12, intensity: 'RPE 8', details: 'Neutral grip. Hits brachialis for arm thickness, easier on the elbows than supinated curls.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress after rowing and hanging' },
      ],
    },
  ],
};

// Day 4 — Lower (Hinge-dominant). RDL leads, then glutes and unilateral
// work. Less direct quad work since Day 2 covered that.
const lowerHingeDay: WorkoutTemplateDay = {
  name: 'Lower Body — Hinge',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 3, reps: 10, format: 'recovery', details: 'Critical for the hinge — spine has to be neutral throughout' },
        { name: 'Hip Opener Flow', sets: 2, reps: 10, format: 'recovery', details: 'Open hips deeply before RDL and lunges' },
      ],
    },
    {
      name: 'Compound Hinge',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 4, reps: 8, intensity: 'progressive', details: 'Hinge pattern, slight knee bend. Feel the hamstrings load as you reach mid-shin.' },
      ],
    },
    {
      name: 'Hypertrophy Lower',
      type: 'strength',
      exercises: [
        { name: 'Hip Thrust', sets: 3, reps: 12, intensity: 'RPE 8', details: 'Bench against your back, barbell over hips. Pause at the top, drive through heels.' },
        { name: 'Walking Lunge', sets: 3, reps: 12, intensity: 'RPE 8', details: 'Per leg. Add dumbbells once bodyweight is too easy.' },
        { name: 'Seated Calf Raise', sets: 4, reps: 15, intensity: 'RPE 8', details: 'Bent-knee complements Day 2 — seated biases the soleus for full lower-leg development.' },
        { name: 'Hanging Leg Raise', sets: 3, reps: 10, intensity: 'RPE 8', details: 'Strict reps, no swing. Lower legs slowly — the eccentric is what builds the abs.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 10, format: 'recovery', details: 'Decompress the spine after hinging' },
        { name: 'Hip Mobility Flow', sets: 1, reps: 10, format: 'recovery', details: 'Re-open hips after the lunges' },
      ],
    },
  ],
};

// Day 5 — Easy cardio + mobility. Not for fat loss specifically; for heart
// health, recovery between lifting days, and aerobic baseline. The
// bodyCompGoal-driven calorie system handles deficit if user wants to cut.
const cardioDay: WorkoutTemplateDay = {
  name: 'Easy Cardio + Mobility',
  sessionType: 'pt',
  estimatedDuration: 40,
  blocks: [
    {
      name: 'Aerobic Base',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Run', duration: '30-40 min', format: 'cardio', intensity: 'Easy', details: 'Conversational pace, Zone 2. Bike, row, or incline walk substitute fine — pick what doesn\'t aggravate the body.' },
      ],
    },
    {
      name: 'Mobility',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 10, format: 'recovery', details: 'Dynamic stretches focused on hips and ankles — the joints lifting compresses.' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const hypertrophy12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12)
    'testing-week-day1': testingDay,
    'testing-week-day2': restDay,
    'testing-week-day3': restDay,
    'testing-week-day4': restDay,
    'testing-week-day5': restDay,

    // Training cycle (W2-11)
    'training-day1': upperPushDay,
    'training-day2': lowerSquatDay,
    'training-day3': upperPullDay,
    'training-day4': lowerHingeDay,
    'training-day5': cardioDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};
